import * as React from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, isNil } from 'lodash';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { Form } from 'components/form';
import FormField from 'components/form/form-field/FormField';
import Input from 'components/inputs/input/Input';
import CrossButton from 'components/buttons/cross-button/CrossButton';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import SVGIcon from 'components/svg-icon/SVGIcon';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import DragAndDrop from 'components/drag-and-drop/DragAndDrop';
import HelpNote from 'components/help-note/HelpNote';

import { sendBulkAttachment, sendBulkMessage } from 'modules/messenger/reducers/messages';
import { fetchAllConversations, fetchConversations } from 'modules/messenger/reducers/conversations';

import {
  LONG_STRING_MAX_LENGTH,
  isArrayRequired,
  isRequired,
  maxLength
} from 'utils/validators';
import { isAccepted, isPending } from 'utils/subcampaign';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { getAttachmentIcon } from 'utils/messages';

import type { MessengerBulkAttachment, MessengerBulkMessage, MessengerConversation, MixedSelectDropdownItem } from 'types';

import type { ChangeEvent, SyntheticEvent } from 'react';

applyTranslations(require('./bulk-message-modal.i18n.json'));

type OwnProps = {
  campaignId: number,
  onCloseModal: () => void
};

type DispatchProps = {
  dispatchSendBulkMessage: (body: MessengerBulkMessage) => Promise<void>,
  dispatchSendBulkAttachment: (body: MessengerBulkAttachment) => Promise<void>,
  dispatchFetchConversations: (campaignId: number) => Promise<void>,
  dispatchFetchAllConversations: (campaignId: number) => Promise<Array<MessengerConversation>>
};

type Props = OwnProps & DispatchProps;

type State = {
  recipients: Array<MixedSelectDropdownItem>,
  acceptedRecipientIds: Array<string>,
  acceptedChecked: boolean,
  pendingRecipientIds: Array<string>,
  pendingChecked: boolean,
  message: string,
  files: Array<File> | undefined | null,
  isModified: boolean,
  conversations: Array<MessengerConversation>
};

class BulkMessageModal extends React.PureComponent<Props, State> {
  state: State = {
    recipients: [],
    acceptedRecipientIds: [],
    acceptedChecked: false,
    pendingRecipientIds: [],
    pendingChecked: false,
    message: '',
    files: null,
    isModified: false,
    conversations: []
  };
  image: HTMLImageElement | undefined | null;

  input: HTMLInputElement | undefined | null;

  fileInput: HTMLInputElement | undefined | null;

  componentDidMount() {
    const { dispatchFetchAllConversations, campaignId } = this.props;

    dispatchFetchAllConversations(campaignId)
      .then(conversations => {
        const recipients = this.conversationToMultiSelectItem(conversations);

        const acceptedRecipientIds = conversations
          .filter(conversation => isAccepted(conversation.subcampaign))
          .map(conversation => conversation.externalId);

        const pendingRecipientIds = conversations
          .filter(conversation => isPending(conversation.subcampaign))
          .map(conversation => conversation.externalId);

        this.setState({
          recipients,
          acceptedRecipientIds,
          acceptedChecked: false,
          pendingRecipientIds,
          pendingChecked: false,
          message: '',
          files: null,
          isModified: false,
          conversations
        });
      });
  }

  conversationToMultiSelectItem(conversations: Array<MessengerConversation>): Array<MixedSelectDropdownItem> {
    return conversations.map(conversation => ({
      identifier: conversation.externalId,
      isDisabled: false,
      isFocused: false,
      isSelected: false,
      label: conversation.subcampaign.creatorProfile.name,
      value: conversation.externalId
    }));
  }

  handleRecipientsBulkChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    const { recipients } = this.state;
    const { name, checked } = event.currentTarget;

    let toggleRecipientsIds: Array<string> = [];

    if (name === 'accepted') {
      toggleRecipientsIds = this.state.acceptedRecipientIds;
    } else if (name === 'pending') {
      toggleRecipientsIds = this.state.pendingRecipientIds;
    } else {
      return;
    }

    const updatedSelectedRecipients = recipients.map((recipient) => ({
      ...recipient,
      isSelected: (toggleRecipientsIds.includes(recipient.identifier)) ? checked : recipient.isSelected
    }));

    this.handleRecipientsChange(updatedSelectedRecipients);
  };

  handleRecipientsChange = (recipients: Array<MixedSelectDropdownItem>) => {
    const selectedIds = recipients.reduce((acc, entry) =>
      ((entry.isSelected) ? [...acc, entry.identifier] : acc), [] as Array<string | number>);

    this.setState(state => {
      const isAllAcceptedRecipientsSelected = state.acceptedRecipientIds.every((id) => selectedIds.includes(id));
      const isAllPendingRecipientsSelected = state.pendingRecipientIds.every((id) => selectedIds.includes(id));

      return {
        recipients,
        acceptedChecked: isAllAcceptedRecipientsSelected,
        pendingChecked: isAllPendingRecipientsSelected,
        isModified: true
      };
    });
  };

  handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      message: event.target.value,
      isModified: true
    });
  };

  handleAttachFile = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;

    this.setState((state) => (
      {
        files: [...(state.files || []), ...Array.from(files ?? [])],
        isModified: true
      }
    ));
  };

  handleDeleteFile = (event: SyntheticEvent<HTMLElement>) => {
    const { currentTarget } = event;
    const index = Number(currentTarget.dataset.index);

    this.setState((state) => {
      const newFiles = [...(state.files || [])];
      newFiles.splice(index, 1);

      return {
        files: newFiles,
        isModified: true
      };
    });
  };

  handleSubmitForm = async () => {
    const { campaignId, onCloseModal, dispatchFetchConversations } = this.props;
    const { message, files, recipients, conversations } = this.state;

    const conversationWithCampaignData = conversations.find(conversation => get(conversation, ['subcampaign', 'campaign', 'brandProfileId'], null));
    const profileId = get(conversationWithCampaignData, ['subcampaign', 'campaign', 'brandProfileId'], null);
    const conversationIds = (recipients || []).reduce((ids, recipient) => {
      if (recipient.isSelected) {
        ids.push(recipient.value);
      }

      return ids;
    }, [] as Array<string>);

    try {
      if (!isNil(profileId) && !isEmpty(message) && conversationIds.length !== 0) {
        await this.handleSendMessage(message, conversationIds, profileId);
      }

      if (!isNil(profileId) && !isNil(files) && files.length > 0 && conversationIds.length !== 0) {
        await Promise.all(files.map((file) =>
          this.handleSendAttachment(file, conversationIds, profileId)
        ));
      }
    } catch (e) {
      logError(e);
    }

    onCloseModal();

    if (campaignId) {
      dispatchFetchConversations(campaignId);
    }
  };

  handleSendMessage = async (message: string, conversationIds: Array<string>, profileId: number): Promise<any> => {
    const { dispatchSendBulkMessage } = this.props;

    return dispatchSendBulkMessage({
      text: message,
      metadata: {
        profile: {
          type: 'brand',
          id: profileId
        }
      },
      conversationIds
    });
  };

  handleSendAttachment = async (file: File, conversationIds: Array<string>, profileId: number): Promise<any> => {
    const { dispatchSendBulkAttachment } = this.props;

    return dispatchSendBulkAttachment({
      attachment: file,
      metadata: {
        profile: {
          type: 'brand',
          id: profileId
        }
      },
      conversationIds
    });
  };

  handleShowFileDialog = () => {
    if (this.fileInput) {
      this.fileInput.click();
    }
  };

  handleDrop = (files: Array<File>) => {
    this.setState((state) => (
      {
        files: [...(state.files || []), ...Array.from(files)],
        isModified: true
      }
    ));
  };

  renderFilesPreview() {
    const { files } = this.state;

    if (!files) {
      return null;
    }

    return (
      <div
        className="bulk-message-modal__preview"
      >
        {
          files.map((file, index) => (
            <div
              className="bulk-message-modal__preview-item"
              key={`preview-${file.name}-${index}`}
            >
              {
                file.type.startsWith('image') ?
                  <img
                    className="bulk-message-modal__preview-image"
                    alt={file.name}
                    src={window.URL.createObjectURL(file)}
                  /> :
                  <div
                    className="bulk-message-modal__preview-icon"
                  >
                    <SVGIcon
                      name={getAttachmentIcon({ mimeType: file.type })}
                      width={36}
                      height={45}
                    />
                  </div>
              }
              <span
                className="bulk-message-modal__preview-name text-overflow-ellipsis"
                title={file.name}
              >
                {file.name}
              </span>
              <div className="bulk-message-modal__preview-cross">
                <CrossButton
                  data={{ index }}
                  type="corner-closer"
                  onClick={this.handleDeleteFile}
                />
              </div>
            </div>
          ))
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const {
      recipients,
      message,
      acceptedChecked,
      pendingChecked,
      acceptedRecipientIds,
      pendingRecipientIds,
      isModified
    } = this.state;

    return (
      <ModalFrame
        title={__t('bulk_message_modal.title')}
      >
        {
          isModified &&
          <PromptExit type="modal" />
        }
        <ModalFrameInner
          narrowContent
        >
          <DragAndDrop onDrop={this.handleDrop}>
            <Form
              onSubmit={this.handleSubmitForm}
            >
              <FormField
                label={__t('bulk_message_modal.recipients_label')}
                labelType="highlighted"
                showRequiredMark
                showValidationIcon={false}
                direction="column"
                fontSize="small"
                validators={[
                  isArrayRequired(recipients.filter(entry => entry.isSelected))
                ]}
              >
                {
                  acceptedRecipientIds.length + pendingRecipientIds.length > 0 &&
                  <div className="bulk-message-modal__bulk-selectors">
                    {
                      acceptedRecipientIds.length > 0 &&
                      <InputCheckbox
                        id="input-checkbox-agreed"
                        label={__t('bulk_message_modal.agreed_label')}
                        onChange={this.handleRecipientsBulkChange}
                        containerClass="bulk-message-modal__bulk-inputs"
                        name="accepted"
                        checked={acceptedChecked}
                      />
                    }
                    {
                      pendingRecipientIds.length > 0 &&
                      <InputCheckbox
                        id="input-checkbox-pending"
                        label={__t('bulk_message_modal.pending_label')}
                        onChange={this.handleRecipientsBulkChange}
                        containerClass="bulk-message-modal__bulk-inputs"
                        name="pending"
                        checked={pendingChecked}
                      />
                    }
                  </div>
                }
                <MixedSelectDropdown
                  withMulti
                  withSelectAll
                  withRemovable
                  withSearchable
                  name="recipients"
                  items={recipients}
                  onItemsToggle={this.handleRecipientsChange}
                  onItemsToggleAll={this.handleRecipientsChange}
                  onItemsRemove={this.handleRecipientsChange}
                  onItemsRemoveAll={this.handleRecipientsChange}
                />
              </FormField>
              <FormField
                label={__t('bulk_message_modal.message_label')}
                labelType="highlighted"
                showValidationIcon={false}
                showRequiredMark
                direction="column"
                fontSize="small"
                validators={[
                  isRequired(message),
                  maxLength(message, LONG_STRING_MAX_LENGTH)
                ]}
              >
                <Input
                  width="responsive"
                  placeholder={__t('bulk_message_modal.message_placeholder')}
                  multiline
                  value={message}
                  onChange={this.handleInputChange}
                />
              </FormField>
              <input
                ref={(ref) => {
                  this.fileInput = ref;
                }}
                multiple
                className="messenger-actions__file"
                onChange={this.handleAttachFile}
                type="file"
              />
              {this.renderFilesPreview()}
              <ModalFrameFooter>
                <div className="bulk-message-modal__actions">
                  <ActionButton
                    color="tertiary"
                    onClick={this.handleShowFileDialog}
                  >
                    {__t('bulk_message_modal.attachment_add_label')}
                  </ActionButton>
                  <ActionButton
                    color="primary"
                    type="submit"
                  >
                    {__t('bulk_message_modal.send_label')}
                  </ActionButton>
                </div>
                <div className="bulk-message-modal__info">
                  <HelpNote
                    message={__t('bulk_message_modal.upload_text')}
                  />
                </div>
              </ModalFrameFooter>
            </Form>
          </DragAndDrop>
        </ModalFrameInner>
      </ModalFrame>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSendBulkMessage: (body) => dispatch(sendBulkMessage(body)),
  dispatchSendBulkAttachment: (body) => dispatch(sendBulkAttachment(body)),
  dispatchFetchConversations: (campaignId) => dispatch(fetchConversations(campaignId, 1, {})),
  dispatchFetchAllConversations: (campaignId: number) => dispatch(fetchAllConversations(campaignId))
});

export default connect(null, mapDispatchToProps)(BulkMessageModal);
